import { GetCookie, SetGeneralCookie, DeleteCookie } from "src/common/cookie";
import { ISurveyStatusResponse } from "src/services/survey/surveyService";
import {
  NPS_SURVEY_KEY,
  NPS_DISMISS_COUNT,
  NPS_NEXT_SHOW_AT,
  CYCLE_COOKIE_TTL_MS,
  MAX_DISMISSALS,
  THIRTY_DAYS_MS,
} from "./constants";

/**
 * Users signed up less than 30 days ago are not yet eligible for the survey.
 * A missing/null createDate (e.g. older accounts predating this field) fails open.
 */
export function isSignupEligible(createDate: string | null | undefined): boolean {
  if (!createDate) return true;
  const signupTime = new Date(createDate).getTime();
  if (Number.isNaN(signupTime)) return true;
  return Date.now() - signupTime >= THIRTY_DAYS_MS;
}

/** Namespace a cookie name by user so different users on the same browser don't share state */
export function scopedCookieName(base: string, userKey: string): string {
  return `${base}_${userKey}`;
}

/** Get current dismiss count from cookie */
export function getDismissCount(userKey: string): number {
  return Number(GetCookie(scopedCookieName(NPS_DISMISS_COUNT, userKey)) || "0");
}

/** Whether this is the last chance (no "Maybe Later" button) */
export function isLastChance(userKey: string): boolean {
  return getDismissCount(userKey) >= MAX_DISMISSALS;
}

/** Reset all NPS cookies for a newly-started survey cycle */
export function startNewCycle(surveyKey: string, userKey: string): void {
  const cycleExpiry = new Date(Date.now() + CYCLE_COOKIE_TTL_MS).toISOString();
  SetGeneralCookie(
    scopedCookieName(NPS_SURVEY_KEY, userKey),
    surveyKey,
    cycleExpiry,
  );
  SetGeneralCookie(
    scopedCookieName(NPS_DISMISS_COUNT, userKey),
    "0",
    cycleExpiry,
  );
  DeleteCookie(scopedCookieName(NPS_NEXT_SHOW_AT, userKey));
}

/** Decide whether the survey should be shown given the latest poll response */
export function evaluateSurveyVisibility(
  status: ISurveyStatusResponse,
  userKey: string,
): {
  show: boolean;
  lastChance: boolean;
} {
  if (!status.shouldShowSurvey) {
    return { show: false, lastChance: false };
  }

  const cookieSurveyKey = GetCookie(scopedCookieName(NPS_SURVEY_KEY, userKey));
  const isNewCycle = !cookieSurveyKey || cookieSurveyKey !== status.surveyKey;

  if (isNewCycle) {
    startNewCycle(status.surveyKey, userKey);
    return { show: true, lastChance: false };
  }

  // Snooze window always wins first — even the dismissal that pushes the
  // count to MAX_DISMISSALS still schedules a real SNOOZE_DURATION_MS wait.
  // Once that window passes there's no dismiss button left to push
  // nextShowAt forward again, so last-chance naturally becomes "always show".
  const nextShowAt = GetCookie(scopedCookieName(NPS_NEXT_SHOW_AT, userKey));
  console.log(
    `Evaluating survey visibility. Dismiss count: ${getDismissCount(
      userKey,
    )}, next show at: ${
      nextShowAt ? new Date(Number(nextShowAt)).toLocaleString() : "never"
    }`,
  );
  if (nextShowAt && Date.now() < Number(nextShowAt)) {
    return { show: false, lastChance: false };
  }

  if (isLastChance(userKey)) {
    return { show: true, lastChance: true };
  }

  return { show: true, lastChance: false };
}
